import React, { useEffect, useState, lazy } from 'react';
import { MapPinIcon, LanguagesIcon, UsersIcon, ImageIcon, CalendarIcon, CheckSquareIcon, ChevronDownIcon, ChevronUpIcon, XIcon, MaximizeIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
interface TestDetailsProps {
  data: ImageData[];
}
export const TestDetails: React.FC<TestDetailsProps> = ({
  data
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);
  const locations = ['Amsterdam Airport Schiphol, Netherlands', 'Paris Charles de Gaulle, France', 'London Heathrow, UK', 'Frankfurt Airport, Germany', 'Dubai International Airport, UAE', 'Abu Dhabi International Airport, UAE'];
  const getVisibleItemCount = () => {
    if (windowWidth >= 1024) {
      return locations.length;
    } else if (windowWidth >= 768) {
      return 8;
    } else {
      return 4;
    }
  };
  const visibleLocations = showAllLocations ? locations : locations.slice(0, getVisibleItemCount());
  const hasMoreLocations = locations.length > getVisibleItemCount();
  const languages = ['English'];
  return <div className="space-y-8" data-id="element-378" data-mp="54gbklmmwvn4">
      {/* Introduction */}
      <div className="flex items-start space-x-4 bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-379" data-mp="52vn9dqwy8xp">
        <div className="space-y-3" data-id="element-380" data-mp="26wurjf5mbq0w">
          <h3 className="font-medium text-[#141617]" data-id="element-381" data-mp="26xxl5uubs6ki">Test Overview</h3>
          <p className="text-base text-[#212326] leading-relaxed" data-id="element-382" data-mp="2ax6ewzpd84g2">
            This report details the findings of a visual asset pre-test
            conducted by Brandpulse for Lagardère Travel Retail. The primary
            objective was to identify the most effective digital pillar creative
            to drive pre-orders, optimising stopping power in a visually busy
            travel environment through data-backed insights.
          </p>
          <p className="text-base text-[#212326] leading-relaxed" data-id="element-383" data-mp="3ja1h8f9tm5fb">
            We leverage passive behavioural measurement to identify your
            top-performing asset with confidence, utilising audience signals,
            engagement points, and a Stopping Power Rating (SPR).
          </p>
        </div>
      </div>

      {/* Test Info Blocks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-384" data-mp="5u3fxy7jtd28">
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-385" data-mp="jstg5mpnzsut">
          <div className="flex items-center space-x-2 mb-4" data-id="element-386" data-mp="eu0849t5cu21">
            <ImageIcon className="h-5 w-5 text-[#D1005D]" data-id="element-387" data-mp="ernjxewspngj" />
            <h3 className="font-medium text-[#141617]" data-id="element-388" data-mp="ygjywgb35yu0">Type</h3>
          </div>
          <p className="text-sm text-[#707070]" data-id="element-389" data-mp="13f51unhq2wm5">
            Attention Testing (Static Image)
          </p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-390" data-mp="pdsmad6h4x0s">
          <div className="flex items-center space-x-2 mb-4" data-id="element-391" data-mp="tf4bihlfrg8g">
            <CalendarIcon className="h-5 w-5 text-[#D1005D]" data-id="element-392" data-mp="fpalvx23wrxo" />
            <h3 className="font-medium text-[#141617]" data-id="element-393" data-mp="fmxxb11ufjbn">Test period</h3>
          </div>
          <p className="text-sm text-[#707070]" data-id="element-394" data-mp="kh25toqrbzeu">7 days</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-395" data-mp="5rw9q9n7upb6">
          <div className="flex items-center space-x-2 mb-4" data-id="element-396" data-mp="aqppk8pz6sei">
            <CheckSquareIcon className="h-5 w-5 text-[#D1005D]" data-id="element-397" data-mp="3svsyy5mz5qkq" />
            <h3 className="font-medium text-[#141617]" data-id="element-398" data-mp="3sxnhxeeor8m6">Completion date</h3>
          </div>
          <p className="text-sm text-[#707070]" data-id="element-399" data-mp="1ogd3db5coyh">June 26, 2026</p>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-400" data-mp="3pvsfs4kig19c">
        <h3 className="font-medium text-[#141617] mb-4" data-id="element-401" data-mp="3g44uklejrggb">Test Assets</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-402" data-mp="3g2bq9crep8bh">
          {data.map(item => <div key={item.id} className="relative group cursor-pointer" onClick={() => setSelectedImage(item.thumbnailUrl)} data-id="element-403" data-mp="3c0qejuqlz02v">
              <div className="w-full h-48 rounded-sm overflow-hidden bg-[#F9F9FB] border border-[#F2F3F5]" data-id="element-404" data-mp="32328i8njy65e">
                <img src={item.thumbnailUrl} alt={item.name} className="w-full h-full object-contain" referrerPolicy="no-referrer" loading="lazy" data-id="element-405" data-mp="2x7d5fvcbq74c" />
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-sm" data-id="element-406" data-mp="rdn4xapbphp8" />
              <div className="absolute top-2 right-2 bg-white/80 p-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-407" data-mp="297um0lhgzcqw">
                <MaximizeIcon className="h-4 w-4 text-[#141617]" data-id="element-408" data-mp="2iz8ieke4jwft" />
              </div>
              <p className="text-xs text-[#707070] mt-2" data-id="element-409" data-mp="2n13tggluxo6s">{item.name}</p>
            </div>)}
        </div>
      </div>

      {/* Targeting Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-410" data-mp="1qcnu5ssy40gh">
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-411" data-mp="1v9e6q65jkdx5">
          <div className="flex items-center space-x-2 mb-4" data-id="element-412" data-mp="1zapxif05j56x">
            <MapPinIcon className="h-5 w-5 text-[#D1005D]" data-id="element-413" data-mp="24aue05127zx5" />
            <h3 className="font-medium text-[#141617]" data-id="element-414" data-mp="24c5kiclw3lry">Location Targeting</h3>
          </div>
          <div className="flex flex-col" data-id="element-415" data-mp="3aqekh6deo47q">
            <p className="text-sm text-[#141617] font-medium mb-2" data-id="element-416" data-mp="3fq12dt46i8mv">
              Global Travel Hubs (Simulated Context)
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-417" data-mp="1fmqezucr97j1">
              {visibleLocations.map(location => <span key={location} className="px-2 py-1 bg-[#F9F9FB] border border-[#F2F3F5] rounded-sm text-xs text-[#707070]" data-id="element-418" data-mp="1bleid4vgrm1c">
                  {location}
                </span>)}
            </div>
            {hasMoreLocations && <button onClick={() => setShowAllLocations(!showAllLocations)} className="mt-3 text-xs font-medium text-[#D1005D] hover:text-[#B0004D] flex items-center transition-colors self-start" data-id="element-419" data-mp="wxthlhm29ywl">
                {showAllLocations ? <>
                    Show less <ChevronUpIcon className="h-3 w-3 ml-1" data-id="element-420" data-mp="11ua4y9lfs98m" />
                  </> : <>
                    +{locations.length - getVisibleItemCount()} more locations{' '}
                    <ChevronDownIcon className="h-3 w-3 ml-1" data-id="element-421" data-mp="95v3kpv89pfr" />
                  </>}
              </button>}
          </div>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-422" data-mp="2pmjbn5b7w5v5">
          <div className="flex items-center space-x-2 mb-4" data-id="element-423" data-mp="2bvxo33atj7qx">
            <LanguagesIcon className="h-5 w-5 text-[#D1005D]" data-id="element-424" data-mp="2fv6kkfx8tjq6" />
            <h3 className="font-medium text-[#141617]" data-id="element-425" data-mp="2ftlmwhfmtko3">Languages</h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-426" data-mp="33jfzqyzyb98c">
            {languages.map(language => <span key={language} className="px-2 py-1 bg-[#F9F9FB] border border-[#F2F3F5] rounded-sm text-xs text-[#707070]" data-id="element-427" data-mp="38ju7efxn4h5u">
                {language}
              </span>)}
          </div>
        </div>
      </div>

      {/* Demographics, Passion Profiles, Life Milestones, Intent Signals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-428" data-mp="1o5224mt1n6rl">
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-429" data-mp="1o2fnk37qjqzv">
          <div className="flex items-center space-x-2 mb-4" data-id="element-430" data-mp="2atieod7w4le4">
            <UsersIcon className="h-5 w-5 text-[#D1005D]" data-id="element-431" data-mp="2aryzkpxz0gdx" />
            <h3 className="font-medium text-[#141617]" data-id="element-432" data-mp="25tdykoaw9sof">Demographics</h3>
          </div>
          <div className="space-y-4" data-id="element-433" data-mp="20vm5hqyik0in">
            <div data-id="element-434" data-mp="20wnhbxncsr1f">
              <p className="text-sm font-medium text-[#141617]" data-id="element-435" data-mp="20x71zm4e1tj5">Gender</p>
              <p className="text-sm text-[#707070]" data-id="element-436" data-mp="2yhjl9glidrqw">Male, Female</p>
            </div>
            <div data-id="element-437" data-mp="2tkt4ss5sku4y">
              <p className="text-sm font-medium text-[#141617]" data-id="element-438" data-mp="2tk9v1yqapc20">Age Groups</p>
              <div className="flex flex-wrap gap-2 mt-2" data-id="element-439" data-mp="2orzyw0yl7fra">
                {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map(age => <span key={age} className="px-2 py-1 bg-[#F9F9FB] border border-[#F2F3F5] rounded-sm text-xs text-[#707070]" data-id="element-440" data-mp="3h92kdccewlnn">
                      {age}
                    </span>)}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-441" data-mp="3otdha9j4ezbb">
          <h3 className="font-medium text-[#141617] mb-2" data-id="element-442" data-mp="3tqn55kqv32x1">
            Affinity Targeting
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-443" data-mp="2jkceovdkokp">
            {['Shoppers', 'Travel', 'Gifts & Occasions', 'Beauty & Personal Care', 'Clothing & Accessories'].map(affinity => <span key={affinity} className="px-2 py-1 bg-[#F9F9FB] border border-[#F2F3F5] rounded-sm text-xs text-[#707070]" data-id="element-444" data-mp="ge38yjon0xv3">
                {affinity}
              </span>)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-445" data-mp="ucjo8z0kfwou">
          <h3 className="font-medium text-[#141617] mb-2" data-id="element-446" data-mp="161royjkien6g">Life Events</h3>
          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-sm text-sm" data-id="element-447" data-mp="1b0cftsdvfui8">
            None
          </span>
        </div>
      </div>

      {/* Package Info */}
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-448" data-mp="1fy4inhg21117">
        <h3 className="font-medium text-[#141617] mb-2" data-id="element-449" data-mp="1ks8qs4123f3v">Package Details</h3>
        <p className="text-sm text-[#707070]" data-id="element-450" data-mp="1poh3zbvp0y8e">Standard Pre-Test Package</p>
      </div>

      {/* Image Lightbox */}
      {selectedImage && <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)} data-id="element-451" data-mp="1ypxb9ar7ok8e">
          <div className="relative w-full max-w-3xl flex items-center justify-center" data-id="element-452" data-mp="23o8jxi5rsop1">
            <img src={selectedImage} alt="Test asset preview" className="max-w-full max-h-[90vh] rounded-sm shadow-2xl object-contain" referrerPolicy="no-referrer" onClick={e => e.stopPropagation()} data-id="element-453" data-mp="4dh0pxrdujxq" />
            <button className="absolute -top-12 right-0 bg-white p-2 rounded-sm shadow-md hover:bg-gray-100 transition-colors" onClick={() => setSelectedImage(null)} aria-label="Close image" data-id="element-454" data-mp="3t5dn2g0yy9cf">
              <XIcon className="h-5 w-5 text-gray-700" data-id="element-455" data-mp="kqc076goclj0" />
            </button>
          </div>
        </div>}
    </div>;
};